import { useMemo, useState } from 'react'
import { useGetInvoiceTotals } from '@cocoonspace/sdk-js/domains/invoices/invoices'
import { capitalize } from '@cocoonspace/shared/utils/capitalize'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { buttonVariants } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@cocoonspace/ui/components/chart'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@cocoonspace/ui/components/select'
import { useLingui } from '@lingui/react/macro'
import { keepPreviousData } from '@tanstack/react-query'
import { ArrowRightIcon } from 'lucide-react'
import { LocalizedLink as Link } from '~/components/localized-link'
import { Area, AreaChart, XAxis, YAxis } from 'recharts'

import { useFormatPrice } from '~/hooks/use-format-price'

const chartConfig = {
	value: {
		color: 'var(--primary)',
		label: 'Total',
	},
} satisfies ChartConfig

export const InvoicesGraph = () => {
	const { t } = useLingui()
	const formatPrice = useFormatPrice()

	const [year, setYear] = useState(new Date().getFullYear())

	const { data: invoiceTotals } = useGetInvoiceTotals(
		{ year },
		{ query: { placeholderData: keepPreviousData } },
	)

	const data = useMemo(() => {
		return Object.entries(invoiceTotals?.data.months || {}).map(
			([month, total]) => ({
				name: capitalize(
					dateTimeUtils()
						.month(Number(month) - 1)
						.format('MMMM'),
				),
				value: total,
			}),
		)
	}, [invoiceTotals])

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex flex-col items-start justify-between md:flex-row md:items-end'>
				<h2
					className='text-3xl font-bold'
					id='invoices'
				>
					{t`Mes factures`}
				</h2>

				<Link
					className={buttonVariants({
						className: 'gap-1',
						variant: 'textPrimary',
					})}
					href='/account/invoices'
				>
					{t`Voir les factures`}
					<ArrowRightIcon className='!size-3' />
				</Link>
			</div>

			<Card className='w-full divide-y'>
				<div className='flex items-center justify-between'>
					<div className='p-6 py-4'>
						<Select
							onValueChange={(value) => setYear(Number(value))}
							value={year.toString()}
						>
							<SelectTrigger>
								<SelectValue placeholder='Année' />
							</SelectTrigger>

							<SelectContent>
								<SelectItem value={new Date().getFullYear().toString()}>
									{new Date().getFullYear()}
								</SelectItem>

								<SelectItem value={(new Date().getFullYear() - 1).toString()}>
									{new Date().getFullYear() - 1}
								</SelectItem>

								<SelectItem value={(new Date().getFullYear() - 2).toString()}>
									{new Date().getFullYear() - 2}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div className='inline-flex flex-row items-center gap-4 p-6 py-4'>
						<div className='bg-primary h-12 w-[8px] rounded' />

						<div className='flex flex-col gap-1'>
							<span className='shrink-0 leading-none'>
								{t`Total cumulé ${year}`}
							</span>

							<span className='flex items-baseline gap-1 font-bold'>
								{formatPrice(invoiceTotals?.data.total ?? 0, 'EUR')}
								<span className='text-xxs'>HT</span>
							</span>
						</div>
					</div>
				</div>

				<ChartContainer
					className='h-[200px] w-full pt-6 pr-6'
					config={chartConfig}
				>
					<AreaChart data={data || []}>
						<defs>
							<linearGradient
								id='area-gradient'
								x1='0'
								x2='0'
								y1='0'
								y2='1'
							>
								<stop
									offset='0%'
									stopColor='var(--color-value)'
									stopOpacity={0.4}
								/>
								<stop
									offset='100%'
									stopColor='var(--color-value)'
									stopOpacity={0}
								/>
							</linearGradient>
						</defs>

						<Area
							dataKey='value'
							fill='url(#area-gradient)'
							stroke='var(--color-value)'
							type='monotone'
						/>

						<ChartTooltip
							content={<ChartTooltipContent />}
							formatter={(value) => formatPrice(value as number, 'EUR')}
						/>

						<XAxis dataKey='name' />

						<YAxis
							tickFormatter={(value) => formatPrice(value as number, 'EUR')}
						/>
					</AreaChart>
				</ChartContainer>
			</Card>
		</div>
	)
}
